import { useState, useRef, useCallback, useEffect } from "react";
import { format } from "date-fns";
import {
  FiX, FiMic, FiMicOff, FiPlay, FiSquare,
  FiBold, FiItalic, FiUnderline, 
  FiTrash2,
} from "react-icons/fi";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import type { EntryPayload } from "../journal/types";

// ── Props ───────────────────────────────────────────────────────────────────

interface EntryEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (entry: EntryPayload, audioBlob?: Blob | null) => Promise<boolean>;
  onDelete?: () => void;
  initialTitle?: string;
  initialContent?: string;
  initialVoiceNoteUrl?: string;
  initialVoiceTranscript?: string;
  isEditing?: boolean;
}

const EntryEditor = ({
  open,
  onClose,
  onSave,
  initialTitle = "",
  initialContent = "",
  initialVoiceNoteUrl = "",
  initialVoiceTranscript = "",
}: EntryEditorProps) => {
  const [title, setTitle] = useState(initialTitle);
  const [showToolbar, setShowToolbar] = useState(false); // Starts hidden for blur logic
  const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>({});
  const [isPlaying, setIsPlaying] = useState(false);

  const [existingVoiceUrl, setExistingVoiceUrl] = useState(initialVoiceNoteUrl);

  const contentRef = useRef<HTMLDivElement>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const initialised = useRef(false);

  // Real voice recorder
  const recorder = useVoiceRecorder();

  const hasVoiceNote = !!recorder.audioUrl || !!existingVoiceUrl;
  const playableUrl = recorder.audioUrl || existingVoiceUrl;

  useEffect(() => {
    if (contentRef.current && !initialised.current) {
      contentRef.current.innerHTML = initialContent;
      initialised.current = true;
    }
  }, [initialContent]);


  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === "Enter") {
    e.preventDefault();
    contentRef.current?.focus();
  }
};
const titleRef = useRef<HTMLTextAreaElement>(null);

const autoResizeTitle = () => {
  const el = titleRef.current;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

useEffect(() => {
  autoResizeTitle();
}, [title]);
  // Check which formats are active at the current cursor position
  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
  }, []);

  const handleSave = useCallback(async () => {
    const html = contentRef.current?.innerHTML || "";
    const text = contentRef.current?.innerText || "";

    
    const finalBlob = recorder.isRecording
      ? await recorder.stop()
      : recorder.audioBlob;

    const willHaveVoice = !!finalBlob || !!existingVoiceUrl;
    if (!text.trim() && !willHaveVoice) return;

    const finalTranscript = recorder.transcript || initialVoiceTranscript;

    const payload: EntryPayload = {
      title: title || format(new Date(), "EEEE, MMM d"),
      content: html,
      hasVoiceNote: willHaveVoice,
      voiceNoteUrl: existingVoiceUrl,
      voiceTranscript: willHaveVoice ? finalTranscript : "",
    };

    // Optimistic close — clear recorder and close editor immediately
    recorder.clear();
    onClose();

    // Fire-and-forget save in background; onSnapshot will update the list
    onSave(payload, finalBlob).then((ok) => {
      if (!ok) console.error("Journal save failed (background)");
    }).catch(() => {});
  }, [title, initialVoiceTranscript, existingVoiceUrl, recorder, onSave, onClose]);

  const applyFormat = (command: string) => {
    document.execCommand(command);
    updateActiveFormats();
  };

  const toggleRecording = async () => {
    if (recorder.isRecording) {
      recorder.stop();
    } else {
      setExistingVoiceUrl("");
      await recorder.start();
    }
  };

  const discardRecording = async () => {
    if (recorder.isRecording) await recorder.stop();
    setExistingVoiceUrl("");
    recorder.clear();
  };

  const togglePlayback = () => {
    if (!playableUrl) return;
    if (!audioPlayerRef.current) {
      audioPlayerRef.current = new Audio(playableUrl);
      audioPlayerRef.current.onended = () => setIsPlaying(false);
    }
    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      if (audioPlayerRef.current.src !== playableUrl) {
        audioPlayerRef.current = new Audio(playableUrl);
        audioPlayerRef.current.onended = () => setIsPlaying(false);
      }
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const removeVoiceNote = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    setIsPlaying(false);
    setExistingVoiceUrl("");
    recorder.clear();
  };

  // Format elapsed seconds as m:ss
  const fmtTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[101] bg-content-bg flex flex-col">
      {/* Top bar */}
    
       <div className="flex items-center justify-between px-6 pt-8 pb-5 border-b-2 border-black/5 ">
          <button 
          onClick={onClose}
          className="text-[18px] font-semibold text-[#6B7280]">
            Cancel
          </button>
          <button 
          onClick={handleSave}
          className="text-[18px] font-semibold text-[#6B7280]">
            Done
          </button>
        </div>

      {/* Editor area */}
      <div className="flex-1 overflow-y-auto px-6 pb-60">
        {/* Title */}
        <textarea
          value={title}
          onKeyDown={handleTitleKeyDown}
          onChange={(e)=>setTitle (e.target.value)}
          ref={titleRef}

          placeholder="Your title here..."
          className="w-full text-4xl font-dm-sans font-semibold leading-tight bg-transparent text-brand-dark placeholder:text-muted-foreground/30 focus:outline-none resize-none mt-6 border-0"
        />

        {/* Content - contentEditable for rich text */}
        <div
          ref={contentRef}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => setShowToolbar(true)}
          onBlur={() => setShowToolbar(false)} // Added Blur logic
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
          data-placeholder="Today, I found myself reflecting on..."
          className="min-h-[300px] text-base leading-relaxed mt-4 focus:outline-none font-dm-sans text-foreground empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/40 empty:before:pointer-events-none"
        />

        {/* Live transcript while recording */}
        {recorder.isRecording && recorder.transcript && (
          <div className="bg-accent/10 rounded-2xl px-4 py-3 mt-4 border border-accent/20">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-dm-sans m-0 mb-1">
              Live transcript
            </p>
            <p className="text-sm leading-relaxed font-dm-sans text-foreground m-0">
              {recorder.transcript}
            </p>
          </div>
        )}

        {/* Voice note indicator / player */}
        {hasVoiceNote && !recorder.isRecording && (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 bg-secondary rounded-2xl px-4 py-3">
              <button
                onClick={togglePlayback}
                className="w-8 h-8 rounded-full journal-gradient flex items-center justify-center border-0 cursor-pointer"
              >
                {isPlaying ? (
                  <FiSquare size={14} className="text-white" />
                ) : (
                  <FiPlay size={14} className="text-white ml-0.5" />
                )}
              </button>
              <div className="flex-1">
                <p className="text-xs font-medium font-dm-sans m-0">
                  {isPlaying ? "Playing…" : "Voice note recorded"}
                </p>
                <p className="text-[10px] text-muted-foreground font-dm-sans m-0">
                  Tap play to listen{recorder.duration > 0 ? ` · ${fmtTime(recorder.duration)}` : ""}
                </p>
              </div>
              <button onClick={removeVoiceNote} className="text-muted-foreground border-0 bg-transparent cursor-pointer">
                <FiX size={16} />
              </button>
            </div>

            {/* Transcript after recording is finished */}
            {(recorder.transcript || initialVoiceTranscript) && (
              <div className="bg-accent/5 rounded-2xl px-4 py-3 border border-border">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-dm-sans m-0 mb-1">
                  Transcript
                </p>
                <p className="text-sm leading-relaxed font-dm-sans text-foreground m-0">
                  {recorder.transcript || initialVoiceTranscript}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Recorder error */}
        {recorder.error && (
          <p className="text-destructive text-xs mt-4 font-dm-sans">{recorder.error}</p>
        )}

        {/* Encouragement text */}
        <p className="text-muted-foreground/40 text-sm mt-8 leading-relaxed font-dm-sans">
          Keep your story unfolding.
          <br />
          Just tap to continue!
        </p>
      </div>

      {/* Formatting toolbar — shifts up when recording status text is visible */}
      {showToolbar && (
        <div
          onMouseDown={(e) => e.preventDefault()} // Added to prevent blur when clicking tools
          className={`fixed left-1/2 -translate-x-1/2 bg-white border border-border rounded-2xl shadow-lg px-2 py-2 flex items-center gap-1 z-[103] transition-all ${
            recorder.isRecording ? "bottom-44" : "bottom-28"
          }`}
        >
          <button
            onClick={() => applyFormat("bold")}
            title="Bold"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors border-0 cursor-pointer ${
              activeFormats.bold
                ? "bg-secondary text-foreground"
                : "text-foreground/50 hover:bg-secondary/60 bg-transparent"
            }`}
          >
            <FiBold size={18} />
          </button>
          <button
            onClick={() => applyFormat("italic")}
            title="Italic"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors border-0 cursor-pointer ${
              activeFormats.italic
                ? "bg-secondary text-foreground"
                : "text-foreground/50 hover:bg-secondary/60 bg-transparent"
            }`}
          >
            <FiItalic size={18} />
          </button>
          <button
            onClick={() => applyFormat("underline")}
            title="Underline"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors border-0 cursor-pointer ${
              activeFormats.underline
                ? "bg-secondary text-foreground"
                : "text-foreground/50 hover:bg-secondary/60 bg-transparent"
            }`}
          >
            <FiUnderline size={18} />
          </button>
        </div>
      )}

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-content-bg/90 backdrop-blur-sm pb-8 pt-4 px-8 z-[102]">
        <div className="flex items-center justify-center gap-3">
          {/* Mic / pause button */}
          <button
            onClick={toggleRecording}
            aria-label={recorder.isRecording ? "Pause recording" : "Start recording"}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all border-0 cursor-pointer ${
              recorder.isRecording
                ? "bg-destructive text-destructive-foreground animate-pulse"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            }`}
          >
            {recorder.isRecording ? <FiMicOff size={20} /> : <FiMic size={20} />}
          </button>

          {/* Delete — only while recording */}
          {recorder.isRecording && (
            <button
              onClick={discardRecording}
              title="Discard recording"
              className="w-10 h-10 rounded-full flex items-center justify-center border-0 cursor-pointer transition-all bg-destructive/10 text-destructive hover:bg-destructive/20"
            >
              <FiTrash2 size={16} />
            </button>
          )}
        </div>

        {/* Recording status — always below the button row */}
        {recorder.isRecording && (
          <p className="text-center text-xs text-destructive font-medium mt-2 animate-pulse font-dm-sans">
            Recording… {fmtTime(recorder.elapsed)}
          </p>
        )}
      </div>
    </div>
  );
};

export default EntryEditor;